/** Supplier ESG Dashboard (Demo Mode only) - replaces the old bare "Supplier ESG" nav link
 * (which pointed straight at the Procurement Suppliers list) with a dedicated executive
 * dashboard: tiles + tabbed tables covering Environmental, Social, Governance, Operational
 * Performance, Spend Analysis, Supplier Comparison and Risk & Actions. Reads from
 * src/mocks/data/supplierEsgDashboardData.ts (UI-only, no backend calls) - see that file's
 * header comment for how it relates to the existing supplierGovernanceData/
 * supplierMasterListData mocks. */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ExpandMore, Nature, Groups, Gavel, LocalShipping, CompareArrows, WarningAmber, Public } from '@mui/icons-material';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import { Section } from '../../../../components/ui/Section';
import {
  GOVERNANCE_CERTIFICATIONS,
  SUPPLIER_ESG_CLIENTS,
  SUPPLIER_ESG_GROUPS,
  SUPPLIER_ESG_REGIONS,
  SUSTAINABILITY_CERTIFICATIONS,
  buildEsgActionItems,
  supplierEsgProfiles,
} from '../../../../mocks/data/supplierEsgDashboardData';
import { SUPPLIER_CATEGORIES } from '../../../../mocks/data/supplierMasterListData';
import { EMPTY_ESG_DASHBOARD_FILTERS, EsgDashboardFilters, EsgDashboardTab, filterEsgProfiles } from './types';
import ExecutiveOverviewTab from './components/ExecutiveOverviewTab';
import EnvironmentalTab from './components/EnvironmentalTab';
import SocialTab from './components/SocialTab';
import GovernanceComplianceTab from './components/GovernanceComplianceTab';
import OperationalPerformanceTab from './components/OperationalPerformanceTab';
import SpendAnalysisTab from './components/SpendAnalysisTab';
import SupplierComparisonTab from './components/SupplierComparisonTab';
import RiskActionsTab from './components/RiskActionsTab';

const RISK_LEVEL_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

const SupplierEsgDashboard = () => {
  const theme = useTheme();
  const { t } = useTranslation('esgDashboard');

  const TABS: { key: EsgDashboardTab; label: string }[] = [
    { key: 'overview', label: t('tabs.overview', 'Executive Overview') },
    { key: 'environmental', label: t('tabs.environmental', 'Environmental') },
    { key: 'social', label: t('tabs.social', 'Social') },
    { key: 'governance', label: t('tabs.governance', 'Governance') },
    { key: 'operational', label: t('tabs.operational', 'Operational Performance') },
    { key: 'spend', label: t('tabs.spend', 'Spend Analysis') },
    { key: 'comparison', label: t('tabs.comparison', 'Supplier Comparison') },
    { key: 'risk', label: t('tabs.risk', 'Risk & Actions') },
  ];
  const [tabValue, setTabValue] = useState<EsgDashboardTab>('overview');
  const [filters, setFilters] = useState<EsgDashboardFilters>(EMPTY_ESG_DASHBOARD_FILTERS);

  const filteredProfiles = useMemo(() => filterEsgProfiles(supplierEsgProfiles, filters), [filters]);
  const actionItems = useMemo(() => buildEsgActionItems(filteredProfiles), [filteredProfiles]);

  const overallEsgScore = filteredProfiles.length
    ? Math.round(filteredProfiles.reduce((sum, p) => sum + p.esgScore, 0) / filteredProfiles.length)
    : 0;
  const environmentalScore = filteredProfiles.length
    ? Math.round(filteredProfiles.reduce((sum, p) => sum + p.environmentalScore, 0) / filteredProfiles.length)
    : 0;
  const socialScore = filteredProfiles.length
    ? Math.round(filteredProfiles.reduce((sum, p) => sum + p.socialScore, 0) / filteredProfiles.length)
    : 0;
  const governanceScore = filteredProfiles.length
    ? Math.round(filteredProfiles.reduce((sum, p) => sum + p.governanceScore, 0) / filteredProfiles.length)
    : 0;
  const highRiskCount = filteredProfiles.filter((p) => p.riskLevel === 'High' || p.riskLevel === 'Critical').length;
  const attentionCount = filteredProfiles.filter((p) => p.esgScore < 60 && p.riskLevel !== 'Critical' && p.riskLevel !== 'High').length;
  const expiringCertsCount = filteredProfiles.filter((p) => p.certificationsExpiringSoonCount > 0 || p.insuranceStatus === 'ExpiringSoon').length;

  const tiles = [
    { label: t('tiles.overallEsgScore', 'Overall ESG score'), value: overallEsgScore, subtitle: t('tiles.overallEsgScoreSubtitle', 'Weighted average'), icon: <Public />, color: '#1976d2' },
    { label: t('tabs.environmental', 'Environmental'), value: environmentalScore, subtitle: t('tiles.scorecardSubtitle', 'Scorecard'), icon: <Nature />, color: '#2e7d32' },
    { label: t('tabs.social', 'Social'), value: socialScore, subtitle: t('tiles.scorecardSubtitle', 'Scorecard'), icon: <Groups />, color: '#9c27b0' },
    { label: t('tabs.governance', 'Governance'), value: governanceScore, subtitle: t('tiles.scorecardSubtitle', 'Scorecard'), icon: <Gavel />, color: '#0288d1' },
    { label: t('tiles.highRiskSuppliers', 'High risk suppliers'), value: highRiskCount, subtitle: t('tiles.highRiskSuppliersSubtitle', 'High or critical risk'), icon: <WarningAmber />, color: '#d32f2f' },
    { label: t('tiles.requiringAttention', 'Requiring attention'), value: attentionCount, subtitle: t('tiles.requiringAttentionSubtitle', 'ESG score below 60'), icon: <WarningAmber />, color: '#ed6c02' },
    { label: t('tiles.certsExpiringSoon', 'Certs expiring soon'), value: expiringCertsCount, subtitle: t('tiles.certsExpiringSoonSubtitle', 'Certification or insurance'), icon: <LocalShipping />, color: '#795548' },
    { label: t('tiles.outstandingActions', 'Outstanding actions'), value: actionItems.length, subtitle: t('tiles.outstandingActionsSubtitle', 'Across all suppliers'), icon: <CompareArrows />, color: '#5e35b1' },
  ];

  const setFilter = <K extends keyof EsgDashboardFilters>(key: K, value: EsgDashboardFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => setFilters(EMPTY_ESG_DASHBOARD_FILTERS);

  const allCertifications = [...SUSTAINABILITY_CERTIFICATIONS, ...GOVERNANCE_CERTIFICATIONS];

  return (
    <PageArea>
      <PageTitle subTitle={t('page.subtitle', 'Supplier sustainability, compliance, risk and ethical performance across the estate')}>
        {t('page.title', 'Supplier ESG Dashboard')}
      </PageTitle>

      <Box sx={{ mt: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          {RISK_LEVEL_OPTIONS.map((risk) => (
            <Chip
              key={risk}
              label={risk}
              color={filters.riskLevel === risk ? 'primary' : 'default'}
              onClick={() => setFilter('riskLevel', filters.riskLevel === risk ? '' : risk)}
            />
          ))}
        </Box>

        <Accordion variant="borderless">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography variant="textSM"
                fontWeight={600}>{t('filters.advancedFilters', 'Advanced Filters')}</Typography>
              <Button variant="secondary"
                size="md"
                onClick={(event) => { event.stopPropagation(); clearFilters(); }}>{t('filters.clearFilters', 'Clear Filters')}</Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container
              spacing={2}>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField select
                  fullWidth
                  size="small"
                  label={t('filters.supplier', 'Supplier')}
                  value={filters.supplierId}
                  onChange={(e) => setFilter('supplierId', e.target.value === '' ? '' : Number(e.target.value))}>
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {supplierEsgProfiles.map((p) => (
                    <MenuItem key={p.supplierId}
                      value={p.supplierId}>{p.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField select
                  fullWidth
                  size="small"
                  label={t('filters.supplierGroup', 'Supplier group')}
                  value={filters.supplierGroup}
                  onChange={(e) => setFilter('supplierGroup', e.target.value)}>
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {SUPPLIER_ESG_GROUPS.map((group) => (
                    <MenuItem key={group}
                      value={group}>{group}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField select
                  fullWidth
                  size="small"
                  label={t('filters.category', 'Category')}
                  value={filters.category}
                  onChange={(e) => setFilter('category', e.target.value)}>
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {SUPPLIER_CATEGORIES.map((category) => (
                    <MenuItem key={category}
                      value={category}>{category}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField select
                  fullWidth
                  size="small"
                  label={t('filters.contract', 'Contract')}
                  value={filters.contractRef}
                  onChange={(e) => setFilter('contractRef', e.target.value)}>
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {supplierEsgProfiles.map((p) => (
                    <MenuItem key={p.contractRef}
                      value={p.contractRef}>{p.contractRef}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField select
                  fullWidth
                  size="small"
                  label={t('filters.client', 'Client')}
                  value={filters.client}
                  onChange={(e) => setFilter('client', e.target.value)}>
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {SUPPLIER_ESG_CLIENTS.map((client) => (
                    <MenuItem key={client}
                      value={client}>{client}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField select
                  fullWidth
                  size="small"
                  label={t('filters.region', 'Region')}
                  value={filters.region}
                  onChange={(e) => setFilter('region', e.target.value)}>
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {SUPPLIER_ESG_REGIONS.map((region) => (
                    <MenuItem key={region}
                      value={region}>{region}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField select
                  fullWidth
                  size="small"
                  label={t('filters.certification', 'Certification')}
                  value={filters.certification}
                  onChange={(e) => setFilter('certification', e.target.value)}>
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {allCertifications.map((cert) => (
                    <MenuItem key={cert}
                      value={cert}>{cert}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField select
                  fullWidth
                  size="small"
                  label={t('filters.riskLevel', 'Risk level')}
                  value={filters.riskLevel}
                  onChange={(e) => setFilter('riskLevel', e.target.value)}>
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {RISK_LEVEL_OPTIONS.map((risk) => (
                    <MenuItem key={risk}
                      value={risk}>{risk}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField fullWidth
                  size="small"
                  type="number"
                  label={t('filters.minEsgScore', 'Min ESG score')}
                  value={filters.minEsgScore}
                  onChange={(e) => setFilter('minEsgScore', e.target.value === '' ? '' : Number(e.target.value))} />
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField fullWidth
                  size="small"
                  type="date"
                  label={t('filters.reviewedFrom', 'Reviewed from')}
                  InputLabelProps={{ shrink: true }}
                  value={filters.reviewedFrom}
                  onChange={(e) => setFilter('reviewedFrom', e.target.value)} />
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <TextField fullWidth
                  size="small"
                  type="date"
                  label={t('filters.reviewedTo', 'Reviewed to')}
                  InputLabelProps={{ shrink: true }}
                  value={filters.reviewedTo}
                  onChange={(e) => setFilter('reviewedTo', e.target.value)} />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Grid container
        spacing={2}
        sx={{ mb: 0 }}>
        {tiles.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.label}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  {tile.icon}
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={tabValue}
        onChange={(_e, value) => setTabValue(value)}
        variant="scrollable"
        sx={{ mb: '-1px', borderBottom: 1, borderColor: 'divider', ml: 4, mr: 4, px: 2 }}
      >
        {TABS.map((tab) => (
          <Tab key={tab.key}
            value={tab.key}
            label={tab.label} />
        ))}
      </Tabs>

      <Section style={{ marginTop: 0, padding: '40px', paddingBottom: 0 }}>
        <Grid container
          spacing={8}>
          <Grid item
            xs={12}
            sx={{ display: tabValue === 'overview' ? 'block' : 'none' }}>
            <ExecutiveOverviewTab profiles={filteredProfiles}
              actionItems={actionItems} />
          </Grid>
          <Grid item
            xs={12}
            sx={{ display: tabValue === 'environmental' ? 'block' : 'none' }}>
            <EnvironmentalTab profiles={filteredProfiles} />
          </Grid>
          <Grid item
            xs={12}
            sx={{ display: tabValue === 'social' ? 'block' : 'none' }}>
            <SocialTab profiles={filteredProfiles} />
          </Grid>
          <Grid item
            xs={12}
            sx={{ display: tabValue === 'governance' ? 'block' : 'none' }}>
            <GovernanceComplianceTab profiles={filteredProfiles} />
          </Grid>
          <Grid item
            xs={12}
            sx={{ display: tabValue === 'operational' ? 'block' : 'none' }}>
            <OperationalPerformanceTab profiles={filteredProfiles} />
          </Grid>
          <Grid item
            xs={12}
            sx={{ display: tabValue === 'spend' ? 'block' : 'none' }}>
            <SpendAnalysisTab profiles={filteredProfiles} />
          </Grid>
          <Grid item
            xs={12}
            sx={{ display: tabValue === 'comparison' ? 'block' : 'none' }}>
            <SupplierComparisonTab profiles={filteredProfiles} />
          </Grid>
          <Grid item
            xs={12}
            sx={{ display: tabValue === 'risk' ? 'block' : 'none', pb: 5 }}>
            <RiskActionsTab profiles={filteredProfiles}
              actionItems={actionItems} />
          </Grid>
        </Grid>
      </Section>
    </PageArea>
  );
};

export default SupplierEsgDashboard;
